import type { CompanyOption } from '../../../../shared/context/GlobalFilterContext'
import { resolveVesselCompanyGroupId } from '../../core/cargoScopeHelpers'
import type { PortConsumptionRow } from './types'

const reportedPortNumber = (value: unknown): number | null => {
  if ((typeof value !== 'string' && typeof value !== 'number') || String(value).trim() === '') return null
  const number = Number(value)
  return Number.isFinite(number) && number >= 0 ? number : null
}

export const dashboardPortConsumption = (reports: any[], vessels: any[]): PortConsumptionRow[] => {
  const names = new Map(vessels.map(v => [Number(v.id), v.fleet_name || v.name]))
  return reports.filter(r => ['SUBMITTED', 'REVIEWED'].includes(r.status)).flatMap(report =>
    (Array.isArray(report.foConsumptionRows) ? report.foConsumptionRows : []).flatMap((row: any, index: number) => {
      if (!row || ![row.atBerthCons, row.atBerthHrs, row.atAnchorageCons, row.atAnchorageHrs]
        .some(value => value != null && String(value).trim() !== '')) return []
      return [{
        key: `${report.id}-${index}`, reportId: report.id, vesselId: report.vesselId,
        vessel: names.get(Number(report.vesselId)) || report.vesselName || 'Vessel name unavailable',
        date: row.date || report.reportDate || '', status: report.status,
        berthFuel: reportedPortNumber(row.atBerthCons), berthHours: reportedPortNumber(row.atBerthHrs),
        anchorageFuel: reportedPortNumber(row.atAnchorageCons), anchorageHours: reportedPortNumber(row.atAnchorageHrs),
      }]
    })
  ).sort((a, b) => (Date.parse(b.date) || 0) - (Date.parse(a.date) || 0) || Number(b.reportId) - Number(a.reportId))
}

export const normalizeDashboardStatus = (value: unknown): string => {
  const status = String(value ?? '').trim().toUpperCase().replace(/[_-]+/g, ' ')
  if (['SEA', 'AT SEA', 'UNDERWAY', 'UNDER WAY', 'SAILING'].includes(status)) return 'At Sea'
  if (['PORT', 'IN PORT', 'AT PORT', 'BERTH', 'BERTHED', 'ALONGSIDE'].includes(status)) return 'In Port'
  if (['ANCHOR', 'AT ANCHOR', 'ANCHORAGE', 'AT ANCHORAGE'].includes(status)) return 'At Anchorage'
  return 'Unknown'
}

export const scopeDashboardVessels = (vessels: any[], company: CompanyOption | null, vesselId?: number, search = '') => {
  const query = search.trim().toLowerCase()
  return vessels.filter(v => {
    if (vesselId && Number(v.id) !== vesselId) return false
    if (company?.type === 'company_group' && resolveVesselCompanyGroupId(v) !== Number(company.id)) return false
    if (company?.type === 'company_admin' && Number(v.companyAdminId ?? v.companyAdmin?.id ?? v.companyId) !== Number(company.id)) return false
    return !query || [v.fleet_name, v.name, v.imoNumber].some(value => String(value ?? '').toLowerCase().includes(query))
  })
}

export const isOpenDashboardDefect = (record: any) =>
  record.active !== false && ['SUBMIT', 'PENDING_APPROVAL', 'APPROVED', 'REJECTED'].includes(String(record.status ?? '').trim().toUpperCase())

// The defect DTO has no due date. This dashboard uses the existing observation-age convention.
export const isOverdueDashboardDefect = (record: any, now = Date.now()) =>
  isOpenDashboardDefect(record) && Number.isFinite(Date.parse(record.dateObserved)) &&
  now - Date.parse(record.dateObserved) > 30 * 86400000

export const dashboardReportName = (report: any): string => {
  const explicit = String(report.reportName ?? report.reportType ?? '').trim()
  if (explicit && !['DRAFT', 'REPORT', 'POSITION_REPORT', 'POSITION REPORT'].includes(explicit.toUpperCase())) {
    return explicit.replace(/_/g, ' ')
  }
  const template = report.assignment?.customTemplate ?? report.assignment?.template
  const fieldIds = new Set((report.values ?? []).map((v: any) => Number(v.field?.id ?? v.field)))
  const matches = (template?.menus ?? []).filter((menu: any) =>
    (menu.submenus ?? []).some((sub: any) => (sub.fields ?? []).some((field: any) => fieldIds.has(Number(field.id)))))
  if (matches.length === 1 && matches[0].name) return matches[0].name
  return template?.name || (explicit && explicit.toUpperCase() !== 'DRAFT' ? explicit.replace(/_/g, ' ') : `Position Report${report.id != null ? ` #${report.id}` : ''}`)
}

export const upcomingDashboardVoyages = <T extends { active: boolean; eta?: string }>(voyages: T[], now = Date.now()) =>
  voyages.filter(v => v.active && Number.isFinite(Date.parse(v.eta || '')) && Date.parse(v.eta!) >= now)
    .sort((a, b) => Date.parse(a.eta!) - Date.parse(b.eta!))

// Only recorded particulars are used here; this is not an attained-CII calculation.
export const recordedCiiDistribution = (records: any[]) => {
  const counts = { a: 0, b: 0, c: 0, d: 0, e: 0 }
  records.forEach(record => {
    const rating = String(record?.ciiRating ?? '').trim().toLowerCase()
    if (record?.hasCiiRating === true && Object.prototype.hasOwnProperty.call(counts, rating)) {
      counts[rating as keyof typeof counts] += 1
    }
  })
  const ratings = Object.entries(counts).filter(([, count]) => count > 0)
  return { counts, rated: Object.values(counts).reduce((sum, count) => sum + count, 0), rating: ratings.length === 1 ? ratings[0][0].toUpperCase() : ratings.length > 1 ? 'Mixed' : 'Not recorded' }
}
